import { useEffect, useState } from "react";
import { Save } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

const FIELDS = [
  ["business_name", "Business name", "input"],
  ["business_secondary_name", "Secondary name", "input"],
  ["phone", "Phone (display)", "input"],
  ["phone_href", "Phone link (tel:…)", "input"],
  ["email", "Public email", "input"],
  ["service_area", "Service area", "input"],
  ["license_number", "License Number", "input"],
  ["website", "Website", "input"],
  ["instagram", "Instagram handle", "input"],
  ["instagram_url", "Instagram URL", "input"],
  ["facebook", "Facebook handle", "input"],
  ["facebook_url", "Facebook URL", "input"],
  ["yelp_url", "Yelp URL", "input"],
  ["homepage_headline", "Homepage headline", "textarea"],
  ["homepage_subheadline", "Homepage subheadline", "textarea"],
  ["main_cta_text", "Main CTA text", "input"],
  ["secondary_cta_text", "Secondary CTA text", "input"],
  ["pricing_disclaimer", "Pricing disclaimer", "textarea"],
  ["footer_text", "Footer text", "textarea"],
  ["sms_alert_phone", "SMS alert phone", "input"],
];

const EMAIL_FIELDS = [
  ["sender_name", "Sender Name", "input"],
  ["sender_email", "Sender Email", "input"],
  ["reply_to_email", "Reply-To Email", "input"],
  ["notification_email", "Notification Email (new requests)", "input"],
];

export default function AdminSettings() {
  const [values, setValues] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/admin/settings").then((r) => setValues(r.data || {})).catch(() => toast.error("Load failed")).finally(() => setLoading(false));
  }, []);

  const set = (k, v) => setValues((prev) => ({ ...prev, [k]: v }));

  const save = async () => {
    try {
      await api.put("/admin/settings", { items: values });
      toast.success("Settings saved");
    } catch { toast.error("Save failed"); }
  };

  return (
    <div data-testid="admin-settings-page">
      <h2 className="text-2xl font-display font-bold text-[#0F172A]">Website Settings</h2>
      <p className="text-sm text-[#475569] mt-1">Edit the business info and headline content used across the public site.</p>

      <div className="mt-6 bg-white rounded-2xl border border-slate-100 p-6">
        {loading ? <div className="text-sm text-[#475569]">Loading…</div> : (
          <div className="grid sm:grid-cols-2 gap-4">
            {FIELDS.map(([key, label, kind]) => (
              <label key={key} className={`block ${kind === "textarea" ? "sm:col-span-2" : ""}`}>
                <span className="block text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1.5">{label}</span>
                {kind === "textarea" ? (
                  <textarea
                    data-testid={`setting-${key}`}
                    rows={3}
                    value={values[key] ?? ""}
                    onChange={(e) => set(key, e.target.value)}
                    className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#1E3A8A]/30 focus:border-[#1E3A8A]"
                  />
                ) : (
                  <input
                    data-testid={`setting-${key}`}
                    type="text"
                    value={values[key] ?? ""}
                    onChange={(e) => set(key, e.target.value)}
                    className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#1E3A8A]/30 focus:border-[#1E3A8A]"
                  />
                )}
              </label>
            ))}
          </div>
        )}
      </div>

      <div className="mt-6 bg-white rounded-2xl border border-slate-100 p-6" data-testid="admin-email-settings-section">
        <h3 className="font-display font-semibold text-[#0F172A]">Email Settings</h3>
        <p className="text-xs text-[#475569] mt-1">Used for the new-request notification + customer confirmation. Owner-only. Resend API key lives in <code className="px-1 py-0.5 rounded bg-slate-100">/app/backend/.env</code> — not exposed here.</p>
        <div className="mt-4 grid sm:grid-cols-2 gap-4">
          {EMAIL_FIELDS.map(([key, label]) => (
            <label key={key} className="block">
              <span className="block text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1.5">{label}</span>
              <input
                data-testid={`setting-${key}`}
                type="text"
                value={values[key] ?? ""}
                onChange={(e) => set(key, e.target.value)}
                className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#1E3A8A]/30 focus:border-[#1E3A8A]"
              />
            </label>
          ))}
        </div>
      </div>

      <div className="mt-6 flex justify-end">
        <button
          onClick={save}
          data-testid="admin-settings-save"
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#1E3A8A] text-white font-semibold hover:bg-[#1E40AF]"
        >
          <Save className="w-4 h-4" /> Save settings
        </button>
      </div>
    </div>
  );
}
